export const queryKeys = {
  matters: {
    all: ['matters'] as const,
    detail: (id: string) => ['matters', id] as const,
    // Issue #698 §A — the documents endpoint now accepts an include_statuses
    // CSV opt-in. The query key includes the (sorted) status set so the
    // default-filter view (completed only) and any opt-in variant (upload
    // polling needs `failed`) cache independently. `documentsPrefix` is the
    // shared base that invalidations must target so both variants drop
    // together when a document is added/renamed/deleted.
    documentsPrefix: (id: string) => ['matters', id, 'documents'] as const,
    documents: (id: string, includeStatuses?: ReadonlyArray<string>) =>
      [
        'matters',
        id,
        'documents',
        { includeStatuses: [...(includeStatuses ?? [])].sort() },
      ] as const,
    workflows: (id: string) => ['matters', id, 'workflows'] as const,
    chatSessions: (id: string) => ['matters', id, 'chat-sessions'] as const,
    // Plan 143: scoped chat session lookup keyed by (matter, scope, sourceId).
    scopedChatSession: (matterId: string, scope: string, sourceId: string) =>
      ['matters', matterId, 'scopedChatSession', scope, sourceId] as const,
    // Plan 174 (#763): per-source scoped chat user-turn counts for the matter,
    // bucketed by scope. One call per matter visit; cached. Invalidated by
    // scoped-thread sends so the badge refreshes after each turn.
    scopedChatCounts: (matterId: string) =>
      ['matters', matterId, 'scoped-chat-counts'] as const,
  },
  documents: {
    detail: (id: string) => ['documents', id] as const,
    clauses: (id: string) => ['documents', id, 'clauses'] as const,
    revisions: (id: string) => ['documents', id, 'revisions'] as const,
    clauseRevisions: (docId: string) => ['documents', docId, 'clause-revisions'] as const,
    changeManifest: (docId: string) => ['documents', docId, 'change-manifest'] as const,
    redlineSegments: (docId: string) => ['documents', docId, 'redline-segments'] as const,
    // Plan 170: Comments layer threaded payload
    comments: (docId: string) => ['documents', docId, 'comments'] as const,
  },
  issues: {
    byProject: (analysisRunId: string) => ['issues', analysisRunId] as const,
    // Plan 180: includeDismissed is part of the cache key so the default
    // (non-dismissed) and "Show dismissed" variants are cached separately.
    // Mutation hooks invalidate the hierarchical prefix ['issues', runId,
    // 'with-modifications'] so both variants refetch together.
    withModifications: (analysisRunId: string, includeDismissed: boolean = false) =>
      ['issues', analysisRunId, 'with-modifications', { includeDismissed }] as const,
    modificationSummary: (analysisRunId: string) => ['issues', analysisRunId, 'summary'] as const,
  },
  comparison: {
    results: (analysisRunId: string) => ['comparison', analysisRunId] as const,
    // Plan 180: includeDismissed in the key (see issues.withModifications).
    documentUnion: (documentId: string, includeDismissed: boolean = false) =>
      ['comparison', 'document-union', documentId, { includeDismissed }] as const,
    // Plan 195: pre-flight similarity check keyed by ordered (base, review).
    // NOT sorted — alignment is conceptually directional (matched_pairs flow
    // base→review). Cache invalidations should use prefix ['compare-preflight'].
    preflight: (baseDocumentId: string, reviewDocumentId: string) =>
      ['compare-preflight', baseDocumentId, reviewDocumentId] as const,
  },
  negotiation: {
    // Plan 180: includeDismissed in the key (see issues.withModifications).
    findings: (analysisRunId: string, includeDismissed: boolean = false) =>
      ['negotiation', analysisRunId, 'findings', { includeDismissed }] as const,
    config: (analysisRunId: string) => ['negotiation', analysisRunId, 'config'] as const,
    redlineSegments: (analysisRunId: string, documentId: string) =>
      ['negotiation', analysisRunId, 'redline-segments', documentId] as const,
    clauseStates: (analysisRunId: string) =>
      ['negotiation', analysisRunId, 'clause-states'] as const,
  },
  drafting: {
    project: (id: string) => ['drafting', id] as const,
    generationStatus: (id: string) => ['drafting', id, 'status'] as const,
  },
  chat: {
    messages: (sessionId: string) => ['chat', sessionId, 'messages'] as const,
  },
  search: {
    results: (query: string, matterId?: string) => ['search', query, matterId] as const,
  },
  sessions: {
    recovery: ['sessions', 'recovery'] as const,
  },
  pipeline: {
    status: (analysisRunId: string) => ['pipeline', analysisRunId, 'status'] as const,
  },
  analysis: {
    status: (analysisRunId: string) => ['analysis', analysisRunId, 'status'] as const,
  },
  playbooks: {
    all: ['playbooks'] as const,
    detail: (id: string) => ['playbooks', id] as const,
    byMatter: (matterId: string) => ['playbooks', 'matter', matterId] as const,
    // Plan 196: bundled position+clause+explanation+playbook lookup.
    positionAttribution: (positionId: string) =>
      ['playbooks', 'position-attribution', positionId] as const,
  },
  playbookMatch: {
    all: ['playbook-match'] as const,
    allSelectedByMatter: (matterId: string) =>
      ['playbook-match', 'all-selected', matterId] as const,
    selectedMatch: (clauseId: string, matterId: string) =>
      ['playbook-match', 'selected', clauseId, matterId] as const,
    candidates: (clauseId: string, matterId: string) =>
      ['playbook-match', 'candidates', clauseId, matterId] as const,
  },
  workbench: {
    all: ['workbench'] as const,
    list: () => ['workbench', 'list'] as const,
    detail: (id: string) => ['workbench', id] as const,
    clauses: (id: string) => ['workbench', id, 'clauses'] as const,
    byMatter: (matterId: string) => ['workbench', 'matter', matterId] as const,
  },
  clauseTypes: {
    all: ['clauseTypes'] as const,
  },
  admin: {
    // Plan 222: includeInternal is part of the key so the External-only vs
    // All-accounts views don't collide in the query cache.
    metricsSummary: (days: number, env?: string, includeInternal?: boolean) => ['admin', 'metrics', 'summary', days, env, includeInternal] as const,
    metricsTrends: (days: number, env?: string, includeInternal?: boolean) => ['admin', 'metrics', 'trends', days, env, includeInternal] as const,
    trustMetrics: (days: number, env?: string, includeInternal?: boolean) => ['admin', 'metrics', 'trust', days, env, includeInternal] as const,
    shadowDetections: (limit: number, days: number, env?: string, includeInternal?: boolean) => ['admin', 'metrics', 'shadow-detections', limit, days, env, includeInternal] as const,
    feedback: (limit: number) => ['admin', 'feedback', limit] as const,
  },
  // Plan 206: pilot terms acceptance gate
  userTerms: ['users', 'me', 'terms'] as const,
};
